import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  AdminRequestError,
  fetchDialerSettings,
  updateRedialPolicy,
  type DialerSettingsResponse,
  type RedialPolicy,
} from './api';

// Only the knobs that exist in both columns, so every row can show saved vs in-force.
const KNOBS: {
  key: keyof DialerSettingsResponse['effective'];
  label: string;
  hint: string;
}[] = [
  {
    key: 'triggers_enabled',
    label: 'Outcome triggers',
    hint: 'Master switch. Off means the rates are measured and never acted on.',
  },
  {
    key: 'wasted_stage1_pct',
    label: 'Wasted — stage 1',
    hint: 'Above this, speculative legs stop. Coverage keeps going out.',
  },
  {
    key: 'wasted_stage2_pct',
    label: 'Wasted — stage 2',
    hint: 'Above this, the pool stops dialling and starts probing.',
  },
  {
    key: 'placement_pct',
    label: 'Placement',
    hint: 'Dials that never reached a phone. One stage — a full stop.',
  },
  { key: 'window_seconds', label: 'Window', hint: 'Seconds of outcomes each rate is read over.' },
  {
    key: 'min_samples',
    label: 'Sample floor',
    hint: 'Below this the rates are neither acted on nor emitted.',
  },
  { key: 'probe_interval_seconds', label: 'Probe interval', hint: 'Seconds between probe dials.' },
  {
    key: 'clean_probes_to_reopen',
    label: 'Clean probes',
    hint: 'Consecutive clean probe outcomes before a pool starts reopening.',
  },
  {
    key: 'mailbox_limit',
    label: 'Mailbox depth',
    hint: 'Backlog past which a pool defers dials. Blank = off.',
  },
  { key: 'backpressure_retry_ms', label: 'Re-poll wait', hint: 'How long a deferred dial waits.' },
  {
    key: 'ring_first_sort',
    label: 'Ring-count order',
    hint: 'Dial never-rung leads first. Changes WHICH leads get dialled, not just when.',
  },
  {
    key: 'ring_bucket_seconds',
    label: 'Ring bucket',
    hint: 'Rounds a lead’s next ring up to a boundary, so leads return in batches. Blank = off.',
  },
  {
    key: 'ring_slack_seconds',
    label: 'Ring slack',
    hint: 'How early a lead may be rung, per rung. Empty = off; the ladder is then a floor.',
  },
  {
    key: 'resting_horizon_seconds',
    label: 'Dark horizon',
    hint: 'Hold SDRs when leads return within this rather than evicting them. 0 = off.',
  },
  {
    key: 'min_seat_seconds',
    label: 'Minimum seat',
    hint: 'How long an SDR is kept before a dark pool may return them to the picker.',
  },
];

// "stopped", not "held": this counts full breaker trips only. The mailbox, shed and
// stage-one gates defer dials without changing admission state, so a pool can be
// deferring hard and not appear here.
export const stoppedLabel = (stopped: number | null, failed: boolean) => {
  if (failed) return 'unknown';
  return stopped === null ? '…' : `${stopped} stopped`;
};

// Unknown is grey, not green: a read that failed must not render as "nothing stopped".
export const stoppedDot = (stopped: number | null, failed: boolean) => {
  if (stopped === null || failed) return 'bg-muted-foreground/40';
  return stopped > 0 ? 'bg-destructive' : 'bg-emerald-500';
};

const show = (value: unknown) => {
  if (value === null || value === undefined) return 'off';
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  // An empty list is a knob's OFF state, and String([]) is '' — which renders as a blank
  // cell and reads as "failed to load" rather than "not set".
  if (Array.isArray(value)) return value.length === 0 ? 'off' : value.join(', ');
  return String(value);
};

const POLL_MS = 30_000;

export function DialAdmissionModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<DialerSettingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Polled, not fetched once: read during an incident, when the knobs and the stopped
  // list both change under the reader.
  useEffect(() => {
    let live = true;

    const read = () =>
      fetchDialerSettings()
        .then((r) => {
          if (!live) return;
          setData(r);
          setError(null);
        })
        .catch((e: unknown) => {
          if (!live) return;
          setError(e instanceof AdminRequestError ? e.message : 'Could not load dial admission');
        });

    read();
    const timer = setInterval(read, POLL_MS);

    return () => {
      live = false;
      clearInterval(timer);
    };
  }, []);

  // aria-modal tells assistive tech the rest of the page is inert, so focus has to move
  // here and stay — otherwise Tab walks the background the dialog claims to have covered.
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onClose();
      if (e.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || active === dialogRef.current)) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      trigger?.focus();
    };
  }, [onClose]);

  const tripped = Object.entries(data?.tripped_pools ?? {});

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Dial admission"
        tabIndex={-1}
        className="bg-card max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg border p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Dial admission</h2>
            <p className="text-muted-foreground text-sm">
              Which gates can stop a pool dialling, and where they are set. Read-only apart from the
              redial ladder below — see <code>docs/dial-admission.md</code> to change the others.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>

        {error && <p className="text-destructive mt-4 text-sm">{error}</p>}
        {!data && !error && <p className="text-muted-foreground mt-4 text-sm">Loading…</p>}

        {data && (
          <>
            <h3 className="mt-5 text-sm font-medium">Pools the breaker has stopped</h3>
            <p className="text-muted-foreground text-xs">
              This node only. <code>hermes.pool_server.admission_state</code> in Datadog is the
              fleet view.
            </p>
            {tripped.length === 0 ? (
              <p className="mt-1 text-sm">None stopped.</p>
            ) : (
              <ul className="mt-1 text-sm">
                {tripped.map(([poolId, reason]) => (
                  <li key={poolId}>
                    Pool {poolId} — <span className="text-destructive">{reason}</span>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="mt-5 text-sm font-medium">Values in force</h3>
            <table className="mt-1 w-full text-sm">
              <thead className="text-muted-foreground text-left text-xs">
                <tr>
                  <th className="py-1">Knob</th>
                  <th className="py-1">Saved</th>
                  <th className="py-1">In force</th>
                </tr>
              </thead>
              <tbody>
                {KNOBS.map(({ key, label, hint }) => {
                  const saved = show(data.settings[key]);
                  const effective = show(data.effective[key]);

                  return (
                    <tr key={key} className="border-t align-top">
                      <td className="py-1.5 pr-3">
                        {label}
                        <p className="text-muted-foreground text-xs">{hint}</p>
                      </td>
                      <td className="py-1.5 pr-3 tabular-nums">{saved}</td>
                      {/* A disagreement means a deploy-time value is pinned on this node,
                          so editing the row would change nothing. */}
                      <td
                        className={cn(
                          'py-1.5 tabular-nums',
                          saved !== effective && 'text-destructive font-medium',
                        )}
                      >
                        {effective}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
